// src/components/modals/MenuModal.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import "../../styles/Modals.css";
import apiUrl from "../../../config";

const MenuModal = ({
                       mode,
                       itemData,
                       onClose,
                       onAdd,
                       onEdit,
                       selectedCategory,
                   }) => {
    // ─── state ─────────────────────────────────────────────────────────────
    const [menuItemsAll, setMenuItemsAll] = useState([]);

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");

    const [category, setCategory] = useState("");
    const [customCategory, setCustomCategory] = useState("");

    const [subcategory, setSubcategory] = useState("");
    const [customSubcategory, setCustomSubcategory] = useState("");

    // Shots group dropdown + custom
    const [groupSelect, setGroupSelect] = useState("");
    const [customGroupTitle, setCustomGroupTitle] = useState("");

    const [price, setPrice] = useState("");
    const [errors, setErrors] = useState({});

    // ─── fetch all menu items ────────────────────────────────────────────────
    useEffect(() => {
        axios
            .get(`${apiUrl}/api/menu`)
            .then((res) => setMenuItemsAll(res.data))
            .catch(console.error);
    }, []);

    // ─── populate on edit / preselect on add ────────────────────────────────
    useEffect(() => {
        if (mode === "edit" && itemData) {
            setName(itemData.name || "");
            setDescription(itemData.description || "");

            const cats = getDistinctCategories(menuItemsAll);
            if (cats.includes(itemData.category)) {
                setCategory(itemData.category);
                setCustomCategory("");
            } else {
                setCategory("addNewCat");
                setCustomCategory(itemData.category || "");
            }

            const subs = getDistinctSubcategories(menuItemsAll, itemData.category);
            if (itemData.subcategory && subs.includes(itemData.subcategory)) {
                setSubcategory(itemData.subcategory);
                setCustomSubcategory("");
            } else if (itemData.subcategory) {
                setSubcategory("addNewSub");
                setCustomSubcategory(itemData.subcategory);
            } else {
                setSubcategory("");
                setCustomSubcategory("");
            }

            // if editing a shot‐item or header, prefill groupSelect/customGroupTitle:
            if (itemData.category === "Drinks" && itemData.subcategory === "Shots") {
                const existing = getDistinctGroupTitles(menuItemsAll);
                if (existing.includes(itemData.groupTitle)) {
                    setGroupSelect(itemData.groupTitle);
                    setCustomGroupTitle("");
                } else {
                    setGroupSelect("__add_new__");
                    setCustomGroupTitle(itemData.groupTitle);
                }
            } else {
                setGroupSelect("");
                setCustomGroupTitle("");
            }

            setPrice(itemData.price || "");
        } else if (mode === "add") {
            if (selectedCategory && selectedCategory !== "All") {
                setCategory(selectedCategory);
            }
            // reset group on add
            setGroupSelect("");
            setCustomGroupTitle("");
        }
    }, [mode, itemData, selectedCategory, menuItemsAll]);

    // ─── helpers ─────────────────────────────────────────────────────────────
    const getDistinctCategories = (items) =>
        Array.from(new Set(items.map((i) => i.category).filter(Boolean)));

    const getDistinctSubcategories = (items, cat) =>
        Array.from(
            new Set(
                items
                    .filter((i) => i.category === cat)
                    .map((i) => i.subcategory)
                    .filter(Boolean)
            )
        );

    const getDistinctGroupTitles = (items) =>
        Array.from(
            new Set(
                items
                    .filter(
                        (i) =>
                            i.category === "Drinks" &&
                            i.subcategory === "Shots" &&
                            i.groupTitle
                    )
                    .map((i) => i.groupTitle)
            )
        );

    const distinctCategories = getDistinctCategories(menuItemsAll);
    const distinctSubcategories = getDistinctSubcategories(
        menuItemsAll,
        category
    );
    const distinctGroupTitles = getDistinctGroupTitles(menuItemsAll);

    // ─── validation ─────────────────────────────────────────────────────────
    const validate = () => {
        const temp = {};
        const isShots = category === "Drinks" && subcategory === "Shots";

        if (!category.trim()) temp.category = "Category is required";
        if (category === "addNewCat" && !customCategory.trim())
            temp.category = "Custom category required";

        if (!subcategory.trim() && subcategory !== "" && category !== "addNewCat")
            temp.subcategory = "Subcategory is required";
        if (subcategory === "addNewSub" && !customSubcategory.trim())
            temp.subcategory = "Custom subcategory required";

        if (isShots) {
            // For header-only Shots, name/price/desc optional
            if (!groupSelect && !customGroupTitle.trim())
                temp.group = "Select or add a group title";
        }

        if (!name.trim()) temp.name = "Name is required";
        if (!description.trim()) temp.description = "Description is required";

        if (!price.trim()) temp.price = "Price is required";
        else if (!/^\d+(\.\d{2})$/.test(price.trim()))
            temp.price = "Must have two decimals (e.g. 12.99)";

        setErrors(temp);
        return Object.keys(temp).length === 0;
    };

    // ─── submit ─────────────────────────────────────────────────────────────
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;

        const formData = new FormData();
        const isShots = category === "Drinks" && subcategory === "Shots";

        // category fields
        formData.append("category", category);
        formData.append("subcategory", subcategory);

        // groupTitle logic
        let finalGroup = "";
        if (isShots) {
            if (groupSelect === "__add_new__") {
                finalGroup = customGroupTitle.trim();
            } else {
                finalGroup = groupSelect;
            }
            formData.append("groupTitle", finalGroup);
        }

        // always append name/desc/price for real item rows
        formData.append("name", name.trim());
        formData.append("description", description.trim());
        formData.append("price", price.trim());

        if (mode === "add") onAdd(formData);
        else if (mode === "edit" && itemData?._id) onEdit(itemData._id, formData);
    };

    // ─── render ─────────────────────────────────────────────────────────────
    return (
        <div
            className="modal-backdrop"
            onClick={(e) =>
                e.target.classList.contains("modal-backdrop") && onClose()
            }
        >
            <div className="modal-container">
                <h2>{mode === "add" ? "Add New Menu Item" : "Edit Menu Item"}</h2>
                <form onSubmit={handleSubmit} className="event-form">
                    {/* CATEGORY */}
                    <div className="form-group">
                        <label>Category:* </label>
                        <select
                            value={category}
                            onChange={(e) => {
                                setCategory(e.target.value);
                                setSubcategory("");
                                setGroupSelect("");
                                setCustomGroupTitle("");
                            }}
                        >
                            <option value="">Select category</option>
                            {distinctCategories.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                            <option value="addNewCat">Add New Category</option>
                        </select>
                        {errors.category && (
                            <span className="error">{errors.category}</span>
                        )}
                    </div>

                    {category === "addNewCat" && (
                        <div className="form-group">
                            <label>Custom Category:* </label>
                            <input
                                type="text"
                                value={customCategory}
                                onChange={(e) => setCustomCategory(e.target.value)}
                            />
                        </div>
                    )}

                    {/* SUBCATEGORY */}
                    <div className="form-group">
                        <label>Subcategory:* </label>
                        <select
                            value={subcategory}
                            onChange={(e) => {
                                setSubcategory(e.target.value);
                                setGroupSelect("");
                                setCustomGroupTitle("");
                            }}
                        >
                            <option value="">Select subcategory</option>
                            {distinctSubcategories.map((s) => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                            <option value="addNewSub">Add New Subcategory</option>
                        </select>
                        {errors.subcategory && (
                            <span className="error">{errors.subcategory}</span>
                        )}
                    </div>

                    {subcategory === "addNewSub" && (
                        <div className="form-group">
                            <label>Custom Subcategory:* </label>
                            <input
                                type="text"
                                value={customSubcategory}
                                onChange={(e) => setCustomSubcategory(e.target.value)}
                            />
                        </div>
                    )}

                    {/* ─── Group Title Dropdown for Shots ───────────────────────────── */}
                    {category === "Drinks" && subcategory === "Shots" && (
                        <div className="form-group">
                            <label>Group Title:</label>
                            <select
                                value={groupSelect}
                                onChange={(e) => {
                                    setGroupSelect(e.target.value);
                                    setCustomGroupTitle("");
                                }}
                            >
                                <option value="">Select existing…</option>
                                {distinctGroupTitles.map((gt) => (
                                    <option key={gt} value={gt}>
                                        {gt}
                                    </option>
                                ))}
                                <option value="__add_new__">+ Add new group…</option>
                            </select>
                            {groupSelect === "__add_new__" && (
                                <input
                                    type="text"
                                    value={customGroupTitle}
                                    onChange={(e) => setCustomGroupTitle(e.target.value)}
                                    placeholder="Enter new group title"
                                />
                            )}
                            {errors.group && <span className="error">{errors.group}</span>}
                        </div>
                    )}

                    {/* NAME */}
                    <div className="form-group">
                        <label>Name:* </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        {errors.name && <span className="error">{errors.name}</span>}
                    </div>

                    {/* DESCRIPTION */}
                    <div className="form-group">
                        <label>Description:* </label>
                        <textarea
                            rows="3"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                        {errors.description && (
                            <span className="error">{errors.description}</span>
                        )}
                    </div>

                    {/* PRICE */}
                    <div className="form-group">
                        <label>Price:* </label>
                        <input
                            type="text"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            placeholder="e.g. 12.99"
                        />
                        {errors.price && <span className="error">{errors.price}</span>}
                    </div>

                    {/* ACTIONS */}
                    <div className="modal-actions">
                        <button type="button" className="btn-cancel" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-submit">
                            {mode === "add" ? "Add Item" : "Save Changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};



// exporting the menu modal
export default MenuModal;


